import {
    FirstPage as FirstPageIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    LastPage as LastPageIcon,
} from '@mui/icons-material';
import PlagiarismIcon from '@mui/icons-material/Plagiarism';
import {
    FormControl,
    IconButton,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    type Theme,
    Typography,
} from '@mui/material';
import { _ } from '/@/locale';
import { MuiSelect } from '/@/mui/select';
import EmptyState from '/@/views/dashboard/grower/components/empty-state';
import { GROWERS_HELP_TOUR_IDS } from '/@/views/dashboard/growers/growers-help-tour-ids';

export interface BayerTableColumn {
    label: string;
    value: string;
    sortable?: boolean;
    sortKey?: string;
    sortOrder?: 'asc' | 'desc';
}

const BayerTable = ({
    columns,
    rows,
    isValidating,
    orderBy,
    order,
    setOrderBy,
    setOrder,
    page,
    pageSize,
    setPage,
    setPageSize,
    emptyState,
    count,
    forceEmptyState = false,
}: {
    columns: BayerTableColumn[];
    rows: React.ReactNode[];
    isValidating?: boolean;
    orderBy?: string;
    order?: 'asc' | 'desc';
    setOrderBy?: (orderBy: string) => void;
    setOrder?: (order: 'asc' | 'desc') => void;
    page: number;
    pageSize: number;
    setPage: (page: number) => void;
    setPageSize: (pageSize: number) => void;
    emptyState?: {
        title: string;
        subtitle?: string;
        icon?: React.ReactNode;
        button?: React.ReactNode;
    };
    count?: number;
    forceEmptyState?: boolean;
}) => {
    const handleChangePageSize = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPageSize(Number.parseInt(event.target.value, 10));
        setPage(1);
    };

    const handleRequestSort = (property: string) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder?.(isAsc ? 'desc' : 'asc');
        setOrderBy?.(property);
    };

    return (
        <TableContainer sx={{ borderRadius: 2, border: (t: Theme) => `1px solid ${t.palette.divider}` }}>
            <Table>
                <TableHead id={GROWERS_HELP_TOUR_IDS.EXPERT}>
                    <TableRow>
                        {columns.map((column) => (
                            <TableCell key={column.label} sx={{ cursor: column.sortable ? 'pointer' : 'default' }}>
                                {column.sortable ? (
                                    <TableSortLabel
                                        active={orderBy === column.sortKey}
                                        direction={orderBy === column.sortKey ? order : 'asc'}
                                        onClick={() => handleRequestSort(column.sortKey)}
                                        data-testid={`table-sort-label-${column.sortKey}`}
                                    >
                                        <Typography color='primary' fontWeight={500}>
                                            {column.label}
                                        </Typography>
                                    </TableSortLabel>
                                ) : (
                                    <Typography color='primary' fontWeight={500}>
                                        {column.label}
                                    </Typography>
                                )}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {!rows.length || isValidating || forceEmptyState ? (
                        <>
                            {console.log(columns)}
                            <TableRow>
                                {columns.map((column, index) => (
                                    <TableCell key={column.label} sx={{ borderBottom: 'none' }}>
                                        <Stack direction='column'>
                                            <Skeleton variant='text' width={!column.label ? 20 : 100} height={20} />
                                            <Skeleton variant='text' width={!column.label ? 20 : 120} height={20} />
                                        </Stack>
                                    </TableCell>
                                ))}
                            </TableRow>
                            {!isValidating && (
                                <TableRow>
                                    <TableCell colSpan={columns.length} sx={{ textAlign: 'center', py: 10 }}>
                                        <EmptyState
                                            title={emptyState?.title || ''}
                                            subtitle={emptyState?.subtitle || ''}
                                            icon={
                                                emptyState?.icon || (
                                                    <PlagiarismIcon color='primary' sx={{ width: 32, height: 32 }} />
                                                )
                                            }
                                            button={emptyState?.button}
                                        />
                                    </TableCell>
                                </TableRow>
                            )}
                        </>
                    ) : (
                        rows
                    )}

                    {/* Pagination */}
                    <TableRow>
                        <TableCell colSpan={columns.length}>
                            <Stack direction='row' justifyContent='end' alignItems='center' spacing={3} py={1}>
                                <Stack direction='row' spacing={1} alignItems='center'>
                                    <Typography variant='body2' color='primary'>
                                        {_('Filas por página:')}
                                    </Typography>
                                    <FormControl size='small' sx={{ minWidth: 80 }}>
                                        <MuiSelect
                                            value={pageSize}
                                            size='small'
                                            onChange={handleChangePageSize}
                                            options={[
                                                { value: 5, label: '5' },
                                                { value: 10, label: '10' },
                                                { value: 20, label: '20' },
                                                { value: 50, label: '50' },
                                                { value: 100, label: '100' },
                                            ]}
                                        />
                                    </FormControl>
                                </Stack>

                                <Typography variant='body1' color='primary'>
                                    {`${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, count || 0)} de ${count || 0}`}
                                </Typography>

                                <Stack direction='row' spacing={1} alignItems='center'>
                                    <IconButton onClick={() => setPage(1)} disabled={page === 1}>
                                        <FirstPageIcon />
                                    </IconButton>
                                    <IconButton onClick={() => setPage(page - 1)} disabled={page === 1}>
                                        <KeyboardArrowDownIcon sx={{ transform: 'rotate(90deg)' }} />
                                    </IconButton>
                                    <IconButton
                                        onClick={() => setPage(page + 1)}
                                        disabled={page >= Math.ceil((count || 0) / pageSize)}
                                    >
                                        <KeyboardArrowDownIcon sx={{ transform: 'rotate(-90deg)' }} />
                                    </IconButton>
                                    <IconButton
                                        onClick={() => setPage(Math.ceil((count || 0) / pageSize))}
                                        disabled={page >= Math.ceil((count || 0) / pageSize)}
                                    >
                                        <LastPageIcon />
                                    </IconButton>
                                </Stack>
                            </Stack>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default BayerTable;
